/**
 * financial-roi.calculator.ts - ROI kalkulace a predikce nákladů
 * 
 * Poskytuje funkce pro:
 * - Výpočet opakujících se nákladů (recurring issues)
 * - Potenciální úspory eliminací top problémů
 * - Dopad snížení prostojů
 * - Predikce budoucích nákladů na základě trendů
 */

import {
    calcPlanItemDowntimeCostKc,
    calcPlanItemLostProfitKc,
    calcPlanItemMaintenanceSplitKc,
} from '@/libs/tpm-analytics/core/financial.calculator';
import { FinancialImpactTrendPointDay } from '@/libs/tpm-analytics/core/types';

// ===== TypeScript interfaces =====

export interface RecurringIssue {
    workplaceId: string | null;
    workplaceName: string;
    faultTypeId: string | null;
    faultTypeName: string;
    frequency: number;
    totalCost: number;
    avgCostPerOccurrence: number;
}

export interface RecurringIssuesCostResult {
    recurringIssues: RecurringIssue[];
    totalRecurringCost: number;
    recurringCostPercent: number; // % z celkových nákladů
}

export interface TopNSavingsPotentialResult {
    potentialSavings: number;
    items: Array<{
        workplaceId: string | null;
        workplaceName: string;
        faultTypeId: string | null;
        faultTypeName: string;
        currentCost: number;
        estimatedSavings: number; // předpokládáme 70% úsporu při eliminaci
    }>;
}

export interface DowntimeReductionImpactResult {
    currentDowntimeCost: number;
    reductionPercent: number;
    potentialSavings: number;
    newDowntimeCost: number;
}

export interface PredictionResult {
    predicted: number;
    confidence: [number, number]; // [lower, upper]
    trend: 'increasing' | 'decreasing' | 'stable';
    trendPercent: number | null;
}

// ===== Funkce =====

/**
 * Vypočítá náklady na opakující se problémy (recurring issues).
 * Problém se považuje za opakující se, pokud se stejná kombinace pracoviště + typ závady
 * vyskytla ≥ minFrequency krát.
 * 
 * @param planItems Seznam odstávek (PlanItem)
 * @param minFrequency Minimální počet výskytů (default 3)
 * @param totalCost Celkové náklady pro výpočet procenta (volitelné)
 * @returns {recurringIssues, totalRecurringCost, recurringCostPercent}
 */
export function calculateRecurringIssuesCost(
    planItems: any[],
    minFrequency = 3,
    totalCost?: number
): RecurringIssuesCostResult {
    // Klíč: "workplaceId|faultTypeId"
    const map = new Map<string, RecurringIssue>();

    const getKey = (wpId: string | null, ftId: string | null) => `${wpId || 'null'}|${ftId || 'null'}`;

    const ensure = (wpId: string | null, wpName: string, ftId: string | null, ftName: string) => {
        const key = getKey(wpId, ftId);
        if (!map.has(key)) {
            map.set(key, {
                workplaceId: wpId,
                workplaceName: wpName || '—',
                faultTypeId: ftId,
                faultTypeName: ftName || '—',
                frequency: 0,
                totalCost: 0,
                avgCostPerOccurrence: 0,
            });
        }
        return map.get(key)!;
    };

    for (const p of planItems) {
        const wpId: string | null = p?.workplaceId ?? p?.workplace?.id ?? null;
        const wpName: string = p?.workplace?.name ?? '—';
        const ftId: string | null = p?.auditFaultTypeId ?? p?.auditFaultType?.id ?? null;
        const ftName: string = p?.auditFaultType?.name ?? '—';
        
        const e = ensure(wpId, wpName, ftId, ftName);
        
        const downKc = calcPlanItemDowntimeCostKc(p);
        const lostKc = calcPlanItemLostProfitKc(p);
        const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
        const cost = downKc + repairKc + maintenanceKc + lostKc;
        
        e.totalCost += cost;
        e.frequency += 1;
    }

    // Filtrujeme pouze opakující se problémy (≥ minFrequency)
    const recurringIssues: RecurringIssue[] = [];
    let totalRecurringCost = 0;

    for (const issue of map.values()) {
        if (issue.frequency >= minFrequency) {
            issue.avgCostPerOccurrence = issue.frequency > 0 ? issue.totalCost / issue.frequency : 0;
            recurringIssues.push({
                ...issue,
                totalCost: Math.round(issue.totalCost),
                avgCostPerOccurrence: Math.round(issue.avgCostPerOccurrence),
            });
            totalRecurringCost += issue.totalCost;
        }
    }

    // Seřadíme podle celkových nákladů (sestupně)
    recurringIssues.sort((a, b) => b.totalCost - a.totalCost);

    const recurringCostPercent = totalCost && totalCost > 0 
        ? (totalRecurringCost / totalCost) * 100 
        : 0;

    return {
        recurringIssues,
        totalRecurringCost: Math.round(totalRecurringCost),
        recurringCostPercent: Math.round(recurringCostPercent * 10) / 10,
    };
}

/**
 * Vypočítá potenciální úspory eliminací top N problémů.
 * Předpokládáme 70% úsporu při kompletní eliminaci problému (realistický odhad).
 * 
 * @param planItems Seznam odstávek (PlanItem)
 * @param n Počet top problémů k analyzování
 * @param savingsRate Míra úspory při eliminaci (0-1, default 0.7 = 70%)
 * @returns {potentialSavings, items}
 */
export function calculateTopNSavingsPotential(
    planItems: any[],
    n = 5,
    savingsRate = 0.7
): TopNSavingsPotentialResult {
    // Klíč: "workplaceId|faultTypeId"
    const map = new Map<string, {
        workplaceId: string | null;
        workplaceName: string;
        faultTypeId: string | null;
        faultTypeName: string;
        totalCost: number;
    }>();

    const getKey = (wpId: string | null, ftId: string | null) => `${wpId || 'null'}|${ftId || 'null'}`;

    const ensure = (wpId: string | null, wpName: string, ftId: string | null, ftName: string) => {
        const key = getKey(wpId, ftId);
        if (!map.has(key)) {
            map.set(key, {
                workplaceId: wpId,
                workplaceName: wpName || '—',
                faultTypeId: ftId,
                faultTypeName: ftName || '—',
                totalCost: 0,
            });
        }
        return map.get(key)!;
    };

    for (const p of planItems) {
        const wpId: string | null = p?.workplaceId ?? p?.workplace?.id ?? null;
        const wpName: string = p?.workplace?.name ?? '—';
        const ftId: string | null = p?.auditFaultTypeId ?? p?.auditFaultType?.id ?? null;
        const ftName: string = p?.auditFaultType?.name ?? '—';
        
        const e = ensure(wpId, wpName, ftId, ftName);
        
        const downKc = calcPlanItemDowntimeCostKc(p);
        const lostKc = calcPlanItemLostProfitKc(p);
        const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
        const cost = downKc + repairKc + maintenanceKc + lostKc;
        
        e.totalCost += cost;
    }

    // Seřadíme podle nákladů a vezmeme top N
    const topIssues = Array.from(map.values())
        .sort((a, b) => b.totalCost - a.totalCost)
        .slice(0, n);

    const items = topIssues.map((issue) => ({
        workplaceId: issue.workplaceId,
        workplaceName: issue.workplaceName,
        faultTypeId: issue.faultTypeId,
        faultTypeName: issue.faultTypeName,
        currentCost: Math.round(issue.totalCost),
        estimatedSavings: Math.round(issue.totalCost * savingsRate),
    }));

    const potentialSavings = items.reduce((sum, item) => sum + item.estimatedSavings, 0);

    return {
        potentialSavings,
        items,
    };
}

/**
 * Vypočítá dopad snížení prostojů o X %.
 * Použijeme jednoduchou lineární projekci.
 * 
 * @param currentDowntimeCost Aktuální náklady na prostoje
 * @param reductionPercent Procento snížení (0-100)
 * @returns {currentDowntimeCost, reductionPercent, potentialSavings, newDowntimeCost}
 */
export function calculateDowntimeReductionImpact(
    currentDowntimeCost: number,
    reductionPercent: number
): DowntimeReductionImpactResult {
    const reductionFactor = reductionPercent / 100;
    const potentialSavings = currentDowntimeCost * reductionFactor;
    const newDowntimeCost = currentDowntimeCost - potentialSavings;

    return {
        currentDowntimeCost: Math.round(currentDowntimeCost),
        reductionPercent,
        potentialSavings: Math.round(potentialSavings),
        newDowntimeCost: Math.round(newDowntimeCost),
    };
}

/**
 * Predikce budoucích nákladů na základě historických trendů.
 * Používá jednoduchou lineární regresi.
 * 
 * @param trendData Historická data trendu (denní body)
 * @param forecastDays Počet dní do budoucna (default 30)
 * @returns {predicted, confidence, trend, trendPercent}
 */
export function predictNextPeriodCosts(
    trendData: FinancialImpactTrendPointDay[],
    forecastDays = 30
): PredictionResult {
    if (trendData.length < 2) {
        return {
            predicted: 0,
            confidence: [0, 0],
            trend: 'stable',
            trendPercent: null,
        };
    }

    // Připravíme data pro regresi
    const points: Array<{ x: number; y: number }> = [];
    
    for (let i = 0; i < trendData.length; i++) {
        const d = trendData[i];
        const totalCost = (d.downtimeKc || 0) + (d.maintenanceKc || 0) + (d.scrapKc || 0) + (d.lostProfitKc || 0);
        points.push({ x: i, y: totalCost });
    }

    // Lineární regrese: y = a + b*x
    const n = points.length;
    const sumX = points.reduce((sum, p) => sum + p.x, 0);
    const sumY = points.reduce((sum, p) => sum + p.y, 0);
    const sumXY = points.reduce((sum, p) => sum + p.x * p.y, 0);
    const sumXX = points.reduce((sum, p) => sum + p.x * p.x, 0);

    const b = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const a = (sumY - b * sumX) / n;

    // Predikce pro další období (průměrná denní hodnota * forecastDays)
    const lastX = n - 1;
    const avgDailyCost = a + b * lastX;
    const predicted = avgDailyCost * forecastDays;

    // Konfidenční interval (±20% jako jednoduchý odhad)
    const confidenceMargin = predicted * 0.2;
    const confidence: [number, number] = [
        Math.max(0, predicted - confidenceMargin),
        predicted + confidenceMargin,
    ];

    // Trend
    let trend: 'increasing' | 'decreasing' | 'stable' = 'stable';
    let trendPercent: number | null = null;

    if (Math.abs(b) > avgDailyCost * 0.01) { // více než 1% změna
        if (b > 0) {
            trend = 'increasing';
        } else {
            trend = 'decreasing';
        }
        
        // Výpočet % trendu - porovnání začátku a konce období
        const firstValue = a + b * 0;
        const lastValue = a + b * lastX;
        if (firstValue > 0) {
            trendPercent = ((lastValue - firstValue) / firstValue) * 100;
        }
    }

    return {
        predicted: Math.round(predicted),
        confidence: [Math.round(confidence[0]), Math.round(confidence[1])],
        trend,
        trendPercent: trendPercent !== null ? Math.round(trendPercent * 10) / 10 : null,
    };
}

/**
 * Analyzuje preventabilní vs nepreventabilní náklady.
 * Preventabilní = opakující se problémy (≥3×) + problémy s krátkou dobou reakce
 * Nepreventabilní = jednorázové problémy + problémy s dlouhou dobou opravy
 * 
 * Toto je zjednodušený odhad pro ROI vizualizaci.
 */
export function categorizePreventableCosts(planItems: any[]): {
    preventable: number;
    nonPreventable: number;
    preventablePercent: number;
} {
    const recurring = calculateRecurringIssuesCost(planItems, 3);
    const preventable = recurring.totalRecurringCost;

    // Celkové náklady
    let totalCost = 0;
    for (const p of planItems) {
        const downKc = calcPlanItemDowntimeCostKc(p);
        const lostKc = calcPlanItemLostProfitKc(p);
        const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
        totalCost += downKc + repairKc + maintenanceKc + lostKc;
    }

    const nonPreventable = Math.max(0, totalCost - preventable);
    const preventablePercent = totalCost > 0 ? (preventable / totalCost) * 100 : 0;

    return {
        preventable: Math.round(preventable),
        nonPreventable: Math.round(nonPreventable),
        preventablePercent: Math.round(preventablePercent * 10) / 10,
    };
}

